import { useEffect, useState, useRef, useCallback } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import PageHeader from "../components/PageHeader";
import MultipleDragDropImage from "../components/MultipleDragDropImage";
import CommonModal from "../components/CommonModal";
import infoCircle from "../assets/info-circle.svg";
import copyIcon from "../assets/copy.svg";
import editIcon from "../assets/edit.svg";
import delIcon from "../assets/trash.svg";
import Lightbox from "../components/Lightbox";
import { requestWithToken } from "../utils/apis";
import { bucketUrl, getFileType, helpPagesData } from "../utils/helper";
import { Spinner } from "react-bootstrap";
import Help from "../components/Help";
import DeleteModal from "../components/DeleteModal";
import "../components/IconUploader.css";
import DeployTrigger from "../components/DeployTrigger";
import checkIcon from "../assets/check-check.svg";

// import "../components/ImageUploader.css"
export default function MediaLibrary() {
  const [show, setShow] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
  });
  const [currentIndex, setCurrentIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const observer = useRef();

  const fetchMediaFiles = async (page = 1) => {
    setLoading(true);
    try {
      const result = await requestWithToken(
        "GET",
        `/api/media/list?page=${page}&limit=${pagination.limit}&type=2`
      );
      if (result?.status === 200) {
        setFiles((prev) =>
          page === 1
            ? result.data.data.list
            : [...prev, ...result.data.data.list]
        );
        setPagination((prev) => ({
          ...prev,
          page,
          total: result.data.data.total,
        }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const lastFileRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && files.length < pagination.total) {
          fetchMediaFiles(pagination.page + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, files, pagination.total, pagination.page]
  );

  const handleDelete = async (id) => {
    try {
      const result = await requestWithToken("DELETE", `/api/media/${id}`);
      if (result?.status === 200) {
        setFiles((prev) => prev.filter((f) => f._id !== id));
        setShow(null);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // const handleCopy = (text) => navigator.clipboard.writeText(text);
  const handleCopy = async (fileUrl, id) => {
    const fullUrl = bucketUrl + fileUrl;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullUrl);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = fullUrl;
        textArea.style.position = "fixed";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }

      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1000);
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  const confirmDelete = async () => {
    if (!fileToDelete) return;
    setDeleteLoader(true);
    await handleDelete(fileToDelete);
    setFileToDelete(null);
    setDeleteLoader(false);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    fetchMediaFiles(1);
  }, []);

  return (
    <>
      <PageHeader pageTitle="Media Library">
        <Help
          title={helpPagesData.pages.title}
          message={helpPagesData.pages.message}
          items={helpPagesData.pages.items}
        />

      </PageHeader>

      <div className="page-container">
        <div className="theme-card mb-3">
          <div className="card-title p-3 pb-0">Drop files to upload</div>
          <div className="p-3">
            <MultipleDragDropImage fetchMediaFiles={() => fetchMediaFiles(1)} />
          </div>
        </div>

        <div className="theme-card mb-3">
          <div className="card-title p-3 pb-0">All Files</div>
          {files.length === 0 && loading && (
            <div className="text-center my-5">
              <Spinner size="md" />
            </div>
          )}
          {files.length > 0 && (
            <div className="all-files px-3 pb-3">
              <div className="file-grid">
                {files.map((f, i) => {
                  if (i === files.length - 1) {
                    return (
                      <div key={i} ref={lastFileRef} className="file-card">
                        {getFileType(f.url) === "video" ? (
                          <video
                            className="video-thumb"
                            src={bucketUrl + f.url}
                            onClick={() => setCurrentIndex(i)}
                          />
                        ) : (
                          <img
                            className="image-thumb"
                            src={bucketUrl + f.url}
                            alt={f.name}
                            onClick={() => setCurrentIndex(i)}
                          />
                        )}
                        <div className="action-box">
                          <button
                            className="icon-btn"
                            onClick={() => setShow(f)}
                          >
                            <img src={editIcon} alt="Edit" />
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => {
                              setShowDeleteModal(true);
                              setFileToDelete(f._id);
                            }}
                          >
                            <img src={delIcon} alt="Delete" />
                          </button>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div key={i} className="file-card">
                        {getFileType(f.url) === "video" ? (
                          <video
                            className="video-thumb"
                            src={bucketUrl + f.url}
                            onClick={() => setCurrentIndex(i)}
                          />
                        ) : getFileType(f.url) === "file" ? (
                          <embed
                            className="pdf-thumb"
                            src={`${bucketUrl + f.url}#page=1&zoom=80`}
                            type="application/pdf"
                            onClick={() => setCurrentIndex(i)}
                          />
                        ) : (
                          <img
                            className="image-thumb"
                            src={bucketUrl + f.url}
                            alt={f.name}
                            onClick={() => setCurrentIndex(i)}
                          />
                        )}
                        <div className="action-box">
                          <button
                            className="icon-btn"
                            onClick={() => setShow(f)}
                          >
                            <img src={editIcon} alt="Edit" />
                          </button>
                          <button
                            className="icon-btn"
                            onClick={() => {
                              setShowDeleteModal(true);
                              setFileToDelete(f._id);
                            }}
                          >
                            <img src={delIcon} alt="Delete" />
                          </button>
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
              {loading && (
                <div className="text-center my-3">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          )}
          {!loading && files.length === 0 && (
            <div className="p-3 text-center text-muted">
              No files uploaded yet.
            </div>
          )}
        </div>

        {currentIndex !== null && (
          <Lightbox
            files={files}
            currentIndex={currentIndex}
            onClose={() => setCurrentIndex(null)}
            onNavigate={(index) => {
              if (index >= 0 && index < files.length) setCurrentIndex(index);
            }}
          />
        )}
      </div>

      {/* ===== Modals ===== */}
      <CommonModal show={show} onClose={() => setShow(null)}>
        {show && (
          <Formik
            initialValues={{
              name: show.name || "",
              alt_text: show.alt_text || "",
              url: bucketUrl + show.url,
              caption: show.caption || "",
            }}
            validationSchema={Yup.object({
              name: Yup.string().required("Required"),
              alt_text: Yup.string(),
              caption: Yup.string(),
            })}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const payload = {
                  name: values.name,
                  alt_text: values.alt_text,
                  caption: values.caption,
                };
                const result = await requestWithToken(
                  "PUT",
                  `/api/media/${show._id}`,
                  payload
                );
                if (result?.status === 200) {
                  setFiles((prev) =>
                    prev.map((f) =>
                      f._id === show._id ? { ...f, ...payload } : f
                    )
                  );
                  setShow(null);
                }
              } catch (error) {
                console.error(error);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <div className="card-header-title">Media Details</div>

                <div className="frm-group">
                  <label className="frm-label">Media Name</label>
                  <Field
                    type="text"
                    name="name"
                    className="frm-input"
                    placeholder="Enter Media Name"
                  />
                </div>

                <div className="frm-group">
                  <label className="frm-label d-flex align-items-center">
                    Alt Text
                    <img
                      className="ms-2"
                      src={infoCircle}
                      alt="Info"
                      title="Alt text eg. About Evaleo"
                    />
                  </label>
                  <Field
                    type="text"
                    name="alt_text"
                    className="frm-input"
                    placeholder="Enter Alt Text"
                  />
                </div>

                <div className="frm-group">
                  <label className="frm-label">Link</label>
                  <div className="input-with-icon right">
                    <Field
                      type="url"
                      name="url"
                      className="frm-input"
                      readOnly
                    />
                    <span
                      className="input-icon"
                      onClick={() => handleCopy(show.url, show._id)}
                      style={{ cursor: "pointer" }}
                    >
                      {copiedId === show._id ? (
                        <img src={checkIcon} alt="copied" />
                      ) : (
                        <img src={copyIcon} alt="copy" />
                      )}
                    </span>
                  </div>
                </div>

                <div className="frm-group">
                  <label className="frm-label">Caption</label>
                  <Field
                    type="text"
                    name="caption"
                    className="frm-input"
                    placeholder="short description"
                  />
                </div>

                <div className="card-footer-box">
                  <button
                    type="button"
                    onClick={() => {
                      setShow(null);
                      setShowDeleteModal(true);
                      setFileToDelete(show._id);
                    }}
                    className="text-danger fw500"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setShow(null)}
                    className="btn-grey ms-auto me-3"
                  >
                    Close
                  </button>
                  <button
                    type="submit"
                    className="btn-black"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <Spinner size="sm" /> : "Save"}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </CommonModal>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={confirmDelete}
        deleting={deleteLoader}
      />
    </>
  );
}
